import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { usePermissions } from './usePermissions';
import { useModalStore } from '../stores/modal';
import { useWebSocketStore } from '../stores/websocket';
import { useInspectionListRefreshStore } from '../stores/inspectionListRefresh';
import { extractApiError } from '../lib/api';
import { equipmentDisplayName, getEvidenceDisplayName } from '@safetyhub/shared';
import { ServerEvents, InspectionDeletedPayload, InspectionCreatedPayload } from '../types/websocket';
import {
  getInspections,
  deleteInspection,
  restoreInspection,
  requestReview,
  startScheduledInspection,
  todayScheduledQueryRootKey,
  buildInspectionStartUrl,
  InspectionListItem,
  InspectionStatus as ApiInspectionStatus,
  InspectionFilters,
  InProgressInspection as ApiInProgressInspection,
  isInspectionSortField,
} from '../lib/inspections';
import { useToastStore } from '../stores/toast';
import { sitesApi, Site } from '../lib/organizations';
import { useInProgressInspections } from './useInspections';
import { getSiteLocations, getSiteEquipment, SiteLocation, SiteEquipment } from '../lib/site-metadata';

// 레이아웃 컴포넌트와 호환되는 상태 타입
type LayoutInspectionStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'PENDING_REVIEW' | 'COMPLETED';

interface FilterState {
  siteId?: string;
  status?: string;
  /** 기간 시작 (현지 YYYY-MM-DD). UTC 변환은 `getInspections` 요청 빌더가 한다. */
  startDate?: string;
  /** 기간 종료 (현지 YYYY-MM-DD). */
  endDate?: string;
  /** 삭제된 점검 포함 여부 */
  includeDeleted?: boolean;
  /** 장소 ID 필터 */
  locationId?: string;
  /** 설비 ID 필터 */
  equipmentId?: string;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
}

/**
 * 점검 목록 페이지 데이터 훅
 * AdaptiveLayout과 함께 사용하기 위한 통합 데이터 훅
 */
const VALID_STATUSES: LayoutInspectionStatus[] = ['SCHEDULED', 'IN_PROGRESS', 'PENDING_REVIEW', 'COMPLETED'];

export function useInspectionListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawStatus = searchParams.get('status');
  const initialStatus = VALID_STATUSES.includes(rawStatus as LayoutInspectionStatus)
    ? (rawStatus as LayoutInspectionStatus)
    : null;
  const queryClient = useQueryClient();
  const { currentMembership, user } = useAuthStore();
  const { openCreateInspection } = useModalStore();
  const { subscribe, isConnected, isAuthenticated } = useWebSocketStore();
  const { refreshTrigger } = useInspectionListRefreshStore();
  const { showToast } = useToastStore();
  const companyId = currentMembership?.companyId;
  const currentUserId = user?.id || '';
  // 목록 행의 「타인 점검 수정·삭제」 어포던스(P4 ②) = 소유자 성질 ∥ `inspection.manage`. 행마다 배정은 레이아웃이
  // `isSiteAssigned(currentMembership, inspection.siteId)`로 따로 거른다(현장 오버라이드는 상세에서 판정). prop 이름 `isOwner`는 호환용.
  const { canManageInspections, isOwner: isOrgOwner } = usePermissions();
  const isOwner = isOrgOwner || canManageInspections;
  const isInitialMount = useRef(true);

  /**
   * 다음 로드가 이어붙이기인지 갈아끼우기인지 — '더 보기'(폰·폴드)만 이어붙이고,
   * 페이지 점프·정렬·필터 변경은 그 페이지로 갈아끼운다. 둘 다 offset만 바꿔 같은
   * 로더를 타므로 의도를 여기에 실어 보낸다.
   */
  const appendNextLoadRef = useRef(false);

  /**
   * 지금 표가 **들고 있는** 질의 — 「이 조회가 첫 로드인가」의 판정 축 (F-40 계열). 판정 근거와
   * 키를 만드는 방법은 `loadInspections` 안 주석 참조.
   */
  const loadedQueryKeyRef = useRef<string | null>(null);

  /**
   * 최신 `loadInspections`를 가리키는 ref (리뷰 W8, 2026-09-14).
   *
   * WS 구독 effect와 `useCallback` 핸들러(복원)는 deps에 `loadInspections`를 넣지 않는다 — 매
   * 렌더 새 함수라 넣으면 구독이 매번 다시 붙는다. 그래서 **구독 시점의 클로저**를 붙잡았고, 그
   * 클로저의 `apiFilters`·`filters`는 낡아 있었다: 필터를 바꾼 뒤 INSPECTION_CREATED가 오면
   * **옛 조건**으로 재조회해 화면이 고른 조건과 다른 목록으로 되돌아갔다. 호출부는 이 ref를 통해
   * 부른다(`loadInspectionsRef.current()`).
   */
  const loadInspectionsRef = useRef<() => Promise<void>>(async () => {});
  /**
   * 요청 순번 — **늦게 끝난 옛 응답이 새 응답을 덮지 않게** (리뷰 W8). 필터 변경 직후 WS
   * 재조회가 겹치면 두 요청이 동시에 나가는데, 먼저 나간 것이 나중에 끝나면 그 결과가 화면을
   * 덮어 옛 조건의 행이 남았다. 응답이 돌아온 시점에 순번이 밀려 있으면 그 응답은 버린다.
   */
  const requestSeqRef = useRef(0);

  // 데이터 상태
  const [inspections, setInspections] = useState<InspectionListItem[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [locations, setLocations] = useState<SiteLocation[]>([]);
  const [equipment, setEquipment] = useState<(SiteEquipment & { displayName: string })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // 필터 상태 (URL 쿼리 파라미터로 초기값 설정)
  const [filters, setFilters] = useState<FilterState>(() => ({
    status: initialStatus ?? undefined,
  }));
  const [apiFilters, setApiFilters] = useState<InspectionFilters>({
    limit: 20,
    offset: 0,
  });

  // 선택된 현장 (새 점검 시작용)
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>();

  // 진행 중 점검 조회
  const { data: inProgressInspections = [] } = useInProgressInspections(
    selectedSiteId,
    companyId
  );

  // 페이지네이션 상태
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
  });

  // 현장 목록 로드
  useEffect(() => {
    if (companyId) {
      loadSites();
    }
  }, [companyId]);

  // 점검 목록 로드
  useEffect(() => {
    if (companyId) {
      loadInspections();
    }
  }, [companyId, apiFilters, filters]);

  // WebSocket 이벤트로 인한 새로고침 트리거 감지
  useEffect(() => {
    // 초기 마운트 시에는 무시 (위의 useEffect에서 이미 로드함)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (companyId && refreshTrigger > 0) {
      loadInspections();
    }
  }, [refreshTrigger]);

  // 점검 삭제 이벤트 구독
  // TODO: WebSocket 핸들러와 handleDeleteInspection의 삭제 후 상태 업데이트 로직이 중복됨.
  //       공통 함수로 추출하여 중복을 제거할 것 (예: applyDeletionToState).
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !companyId) return;

    const unsubscribe = subscribe<InspectionDeletedPayload>(
      ServerEvents.INSPECTION_DELETED,
      (payload) => {
        if (payload.companyId === companyId) {
          if (filters.includeDeleted) {
            // "삭제된 점검 보기"가 체크되어 있으면 deletedAt만 업데이트
            setInspections((prev) =>
              prev.map((i) =>
                i.id === payload.inspectionId
                  ? { ...i, deletedAt: new Date().toISOString() }
                  : i
              )
            );
          } else {
            // 체크되어 있지 않으면 목록에서 제거 + 같은 사이트의 최신 점검 갱신
            setInspections((prev) => {
              const deleted = prev.find((i) => i.id === payload.inspectionId);
              const filtered = prev.filter((i) => i.id !== payload.inspectionId);
              if (deleted) {
                const sameSite = filtered
                  .filter((i) => i.siteId === deleted.siteId && !i.deletedAt)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                if (sameSite.length > 0) {
                  const newLatestId = sameSite[0].id;
                  return filtered.map((i) =>
                    i.id === newLatestId ? { ...i, isLatestInspection: true } : i
                  );
                }
              }
              return filtered;
            });
            // 총 개수 업데이트
            setPagination((prev) => ({
              ...prev,
              totalCount: Math.max(0, prev.totalCount - 1),
              totalPages: Math.max(1, Math.ceil((prev.totalCount - 1) / (apiFilters.limit || 20))),
            }));
          }
          // 진행 중 점검 쿼리 무효화
          queryClient.invalidateQueries({ queryKey: ['inProgressInspections', companyId] });
        }
      }
    );

    return () => unsubscribe();
  }, [isConnected, isAuthenticated, companyId, subscribe, queryClient, apiFilters.limit, filters.includeDeleted]);

  // 점검 생성 이벤트 구독
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !companyId) return;

    const unsubscribe = subscribe<InspectionCreatedPayload>(
      ServerEvents.INSPECTION_CREATED,
      (payload) => {
        if (payload.companyId === companyId) {
          // 목록 새로고침 — 구독 시점 클로저가 아니라 **지금의** 필터로(W8).
          loadInspectionsRef.current();
          // 진행 중 점검 쿼리 무효화
          queryClient.invalidateQueries({ queryKey: ['inProgressInspections', companyId] });
        }
      }
    );

    return () => unsubscribe();
  }, [isConnected, isAuthenticated, companyId, subscribe, queryClient]);

  // 현장 선택 시 장소/설비 로드
  useEffect(() => {
    if (companyId && filters.siteId) {
      loadSiteMetadata(filters.siteId);
    } else {
      setLocations([]);
      setEquipment([]);
    }
  }, [companyId, filters.siteId]);

  const loadSites = async () => {
    if (!companyId) return;
    try {
      const data = await sitesApi.findAll(companyId);
      setSites(data);
    } catch {
      // 현장 로드 실패는 무시
    }
  };

  const loadSiteMetadata = async (siteId: string) => {
    if (!companyId) return;
    try {
      const [locs, equip] = await Promise.all([
        getSiteLocations(companyId, siteId),
        getSiteEquipment(companyId, siteId),
      ]);
      setLocations(locs.filter(l => l.isActive));
      // 표시 이름은 shared 한 벌(⑥ ⓑ — 하위 설비는 「부모 › 이름 (관리번호)」 · 설비 대상 라벨과 같은 함수) — 필터 화면은 이 값만 그린다
      setEquipment(equip.filter(e => e.isActive).map((e) => ({ ...e, displayName: equipmentDisplayName(e) })));
    } catch {
      // 장소/설비 로드 실패는 무시
    }
  };

  const loadInspections = async () => {
    if (!companyId) return;

    // await 전에 읽어 소비한다 — 로드가 겹쳐도 '더 보기' 한 번이 두 번 반영되지 않게.
    const isAppend = appendNextLoadRef.current;
    appendNextLoadRef.current = false;

    const requestFilters: InspectionFilters = {
      ...apiFilters,
      status: (filters.status as ApiInspectionStatus) || undefined,
      siteId: filters.siteId || undefined,
      includeDeleted: filters.includeDeleted || false,
      locationId: filters.locationId || undefined,
      equipmentId: filters.equipmentId || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    };
    /**
     * 「이 조회가 **같은 질의를 다시** 부르는 것인가」 — 실시간 갱신을 조용히 하는 판정 축
     * (F-40 계열, 2026-09-12).
     *
     * 종전에는 매번 `setIsLoading(true)`라, INSPECTION_CREATED가 오거나 삭제·복원 뒤 재조회할
     * 때마다 `InspectionTable`이 표를 통째로 「로딩 중...」으로 갈아 끼웠다. 이미 같은 질의의
     * 결과를 들고 있으면 데이터만 조용히 교체한다.
     *
     * 스피너를 그대로 두는 경우는 **보여 줄 것이 그 질의에 대해 아직 없을 때**다: 최초 로드,
     * 필터·정렬 변경, 페이지 점프, 그리고 '더 보기'(offset이 달라 키가 바뀐다 — 그동안 버튼이
     * `isLoading`으로 잠겨야 연타로 한 페이지를 건너뛰지 않는다).
     *
     * 키는 **서버로 보내는 그 객체**에서 파생시킨다 — 필터 필드를 손으로 열거하면 나중에 필드가
     * 늘 때 그 축이 조용히 빠져 「필터를 바꿨는데 옛 목록이 그대로」가 된다.
     */
    const queryKey = JSON.stringify([companyId, requestFilters]);
    const isFirstLoadForQuery = loadedQueryKeyRef.current !== queryKey;

    // 이 요청의 순번. 응답이 돌아왔을 때 더 새 요청이 나갔으면 이 응답은 버린다(W8).
    const seq = ++requestSeqRef.current;
    const isStale = () => seq !== requestSeqRef.current;

    if (isFirstLoadForQuery) setIsLoading(true);
    setError('');
    try {
      const response = await getInspections(companyId, requestFilters);
      if (isStale()) return;

      if (response.success && response.data) {
        const page = response.data;
        // 이 질의의 결과를 이제 들고 있다 — 다음 재조회부터는 조용히.
        loadedQueryKeyRef.current = queryKey;
        setInspections((prev) => {
          if (!isAppend) return page;
          // 같은 offset으로 두 번 불릴 수 있어(StrictMode 이중 실행·재시도) id로 걸러 붙인다.
          const seen = new Set(prev.map((i) => i.id));
          return [...prev, ...page.filter((i) => !seen.has(i.id))];
        });
        const total = response.meta?.total || page.length;
        const limit = apiFilters.limit || 20;
        const offset = apiFilters.offset || 0;

        setPagination({
          currentPage: Math.floor(offset / limit) + 1,
          totalPages: Math.max(1, Math.ceil(total / limit)),
          totalCount: total,
          hasMore: offset + page.length < total,
        });
      } else {
        setError(response.error?.message || '점검 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      if (isStale()) return;
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      // 조용한 갱신은 애초에 올리지 않았으므로 내리지도 않는다 — 첫 로드가 진행 중일 때
      // 뒤늦게 끝난 조용한 갱신이 그 스피너를 대신 걷어 버리면 빈 표가 노출된다.
      // 버린 응답도 스피너를 건드리지 않는다 — 그 스피너는 더 새 요청의 것이다.
      if (isFirstLoadForQuery && !isStale()) setIsLoading(false);
    }
  };
  loadInspectionsRef.current = loadInspections;

  // 필터 변경
  const handleFilterChange = useCallback((newFilters: FilterState) => {
    // 현장이 변경되면 장소/설비 필터 초기화
    const prevSiteId = filters.siteId;
    const resetLocationEquipment = newFilters.siteId !== prevSiteId
      ? { locationId: undefined, equipmentId: undefined }
      : {};
    setFilters({ ...newFilters, ...resetLocationEquipment });
    setApiFilters(prev => ({ ...prev, offset: 0 }));

    // 현장 선택 시 selectedSiteId도 업데이트
    if (newFilters.siteId) {
      setSelectedSiteId(newFilters.siteId);
    }
  }, [filters.siteId]);

  // 더 보기 (폰·폴드) — 다음 페이지를 현재 목록 뒤에 이어붙인다.
  const handleLoadMore = useCallback(() => {
    appendNextLoadRef.current = true;
    setApiFilters(prev => ({
      ...prev,
      offset: (prev.offset || 0) + (prev.limit || 20),
    }));
  }, []);

  // 페이지 점프 (데스크톱 표) — 그 페이지로 목록을 갈아끼우고 표 머리로 되돌린다.
  const handlePageChange = useCallback((page: number) => {
    const limit = apiFilters.limit || 20;
    const target = Math.min(Math.max(1, page), Math.max(1, pagination.totalPages));
    const nextOffset = (target - 1) * limit;
    if (nextOffset === (apiFilters.offset || 0)) return;
    setApiFilters(prev => ({ ...prev, offset: nextOffset }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [apiFilters.limit, apiFilters.offset, pagination.totalPages]);

  /**
   * 정렬 (데스크톱 표 헤더) — 서버가 전체 결과를 정렬한다. 현재 페이지만 클라에서 정렬하면
   * "이름순 첫 페이지"가 전체 첫 20건이 아니라 최신 20건을 이름순으로 늘어놓은 것이 된다.
   * 같은 축을 다시 누르면 방향만 뒤집고, 축이 바뀌면 내림차순부터 시작한다.
   */
  const handleSort = useCallback((field: string) => {
    if (!isInspectionSortField(field)) return;
    setApiFilters(prev => ({
      ...prev,
      offset: 0,
      sortField: field,
      sortDirection:
        prev.sortField === field && prev.sortDirection === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  // 새로고침
  const handleRefresh = useCallback(async () => {
    await loadInspections();
  }, [loadInspections]);

  // 점검 상세 보기
  const handleViewInspection = useCallback((id: string) => {
    navigate(`/inspections/${id}`);
  }, [navigate]);

  // 점검 삭제 - 성공 시 true, 실패 시 에러 메시지 반환
  const handleDeleteInspection = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!companyId) return { success: false, error: '조직 정보가 없습니다.' };

    try {
      const response = await deleteInspection(id, companyId);
      if (response.success) {
        if (filters.includeDeleted) {
          // "삭제된 점검 보기"가 체크되어 있으면 deletedAt만 업데이트
          setInspections(prev =>
            prev.map(i =>
              i.id === id
                ? { ...i, deletedAt: new Date().toISOString() }
                : i
            )
          );
        } else {
          // 체크되어 있지 않으면 목록에서 제거 + 같은 사이트의 최신 점검 갱신
          setInspections(prev => {
            const deleted = prev.find(i => i.id === id);
            const filtered = prev.filter(i => i.id !== id);
            if (deleted) {
              // 삭제된 점검과 같은 사이트의 남은 점검 중 최신을 isLatestInspection=true로 갱신
              const sameSite = filtered
                .filter(i => i.siteId === deleted.siteId && !i.deletedAt)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
              if (sameSite.length > 0) {
                const newLatestId = sameSite[0].id;
                return filtered.map(i =>
                  i.id === newLatestId ? { ...i, isLatestInspection: true } : i
                );
              }
            }
            return filtered;
          });
          // 총 개수 업데이트
          setPagination((prev) => ({
            ...prev,
            totalCount: Math.max(0, prev.totalCount - 1),
            totalPages: Math.max(1, Math.ceil((prev.totalCount - 1) / (apiFilters.limit || 20))),
          }));
        }
        queryClient.invalidateQueries({ queryKey: ['inspections'] });
        return { success: true };
      } else {
        const errorMsg = response.error?.message || '삭제에 실패했습니다.';
        showToast(errorMsg, 'error');
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
      return { success: false, error: apiError.message };
    }
  }, [companyId, queryClient, apiFilters.limit, filters.includeDeleted, showToast]);

  // 점검 복원 - 성공 시 true, 실패 시 에러 메시지 반환
  const handleRestoreInspection = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!companyId) return { success: false, error: '조직 정보가 없습니다.' };

    try {
      const response = await restoreInspection(id, companyId);
      // 재조회는 ref로 — 이 useCallback은 deps에 loadInspections가 없어 생성 시점 필터를 붙잡는다(W8).
      if (response.success) {
        // 목록 새로고침
        await loadInspectionsRef.current();
        queryClient.invalidateQueries({ queryKey: ['inspections'] });
        return { success: true };
      } else {
        // 실패 시에도 목록 새로고침 (영구 삭제된 항목 제거)
        await loadInspectionsRef.current();
        return { success: false, error: response.error?.message || '복원에 실패했습니다.' };
      }
    } catch (err) {
      const apiError = extractApiError(err);
      // 실패 시에도 목록 새로고침 (영구 삭제된 항목 제거)
      await loadInspectionsRef.current();
      return { success: false, error: apiError.message };
    }
  }, [companyId, queryClient]);

  // 새 점검 시작 클릭 (모달 열기)
  const handleStartNewClick = useCallback(() => {
    openCreateInspection();
  }, [openCreateInspection]);

  // 새 점검 시작 (특정 현장)
  const handleStartNew = useCallback((siteId: string) => {
    // 점검 생성 페이지로 이동하거나 모달 열기
    navigate(`/inspections/new?siteId=${siteId}`);
  }, [navigate]);

  // 점검 계속하기
  const handleContinueInspection = useCallback((inspectionId: string) => {
    navigate(`/inspections/${inspectionId}`);
  }, [navigate]);

  // 삭제된 점검 포함 토글
  const handleToggleIncludeDeleted = useCallback((include: boolean) => {
    setFilters(prev => ({ ...prev, includeDeleted: include }));
    setApiFilters(prev => ({ ...prev, offset: 0 }));
  }, []);

  // 검토 요청
  const handleRequestReview = useCallback(async (inspectionId: string) => {
    if (!companyId) return;

    try {
      const response = await requestReview(inspectionId, companyId);
      if (response.success && response.data) {
        const { notifiedUsers, totalPending } = response.data;
        if (notifiedUsers.length > 0) {
          showToast(`${notifiedUsers.length}명에게 ${totalPending}건의 검토 요청을 보냈습니다.`, 'success');
        } else {
          showToast('검토 대기 중인 사진이 없습니다.', 'info');
        }
      } else {
        showToast(response.error?.message || '검토 요청에 실패했습니다.', 'error');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    }
  }, [companyId, showToast]);

  // 예약 점검 시작 확인 모달 상태
  const [startScheduledConfirmOpen, setStartScheduledConfirmOpen] = useState(false);
  const [startScheduledId, setStartScheduledId] = useState<string | null>(null);

  // 예약 점검 시작 버튼 클릭 → 모달 열기
  const handleStartScheduled = useCallback((id: string) => {
    setStartScheduledId(id);
    setStartScheduledConfirmOpen(true);
  }, []);

  // 모달에서 확인 → 실제 API 호출
  const handleConfirmStartScheduled = useCallback(async () => {
    if (!companyId || !startScheduledId) return;
    const targetId = startScheduledId;
    setStartScheduledConfirmOpen(false);
    try {
      const response = await startScheduledInspection(targetId, companyId);
      if (response.success) {
        showToast('점검이 시작되었습니다.', 'success');
        // 오늘 예정된 점검 위젯(대시보드)에서 즉시 제거 (WebSocket emit 누락/지연 대비)
        // 키는 `lib/inspections.ts`의 루트 헬퍼 — 리터럴을 적으면 그 문자열을 고칠 때 조용히
        // 아무것도 무효화하지 않는다(2026-10-05).
        queryClient.invalidateQueries({ queryKey: todayScheduledQueryRootKey() });
        // 상세 페이지로 이동하면서 현장 담당자 모달 자동 오픈 신호 전달
        navigate(buildInspectionStartUrl(targetId));
      } else {
        showToast(response.error?.message || '점검 시작에 실패했습니다.', 'error');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setStartScheduledId(null);
    }
  }, [companyId, startScheduledId, showToast, navigate, queryClient]);

  // 목록 아이템을 레이아웃용 형식으로 변환
  const inspectionListItems = inspections.map(inspection => ({
    id: inspection.id,
    siteName: inspection.site.name,
    siteId: inspection.siteId,
    inspectionDate: inspection.createdAt,
    // 담당자 = PRIMARY collaborator의 signerName ?? joinSnapshotName 우선, 없으면 라이브 라벨
    inspectorName: getEvidenceDisplayName(
      (() => {
        const primary = inspection.collaborators?.find((c) => c.role === 'PRIMARY');
        return primary?.signerName ?? (primary as any)?.joinSnapshotName ?? null;
      })(),
      { name: inspection.inspector?.member?.name, deletedAt: inspection.inspector?.deletedAt },
      '-',
    ),
    inspectorId: inspection.inspectorId,
    photoCount: inspection._count.photos,
    findingCount: inspection._count.findings,
    // ⚠️ **이 한 줄이 「(모범 N)」 배지의 유일한 공급원이다 — 지우면 배지가 영영 숨는다.**
    // 세 레이아웃(`InspectionTable:327`·Fold`:468`·Phone`:452`)의 게이트가
    // `bestPracticeCount != null && > 0`이라, 안 실으면 `undefined`로 떨어져 **조용히** 사라진다
    // (2026-02-22 배지 신설 이후 7개월간 그 상태였고 tsc·spec 어느 쪽도 못 봤다 — 옵셔널 prop이다).
    // 출처는 서버 `findAll`의 파생값(`countVisibleFindingTypes`)이고 칼럼이 아니다.
    // 탐지기: `__tests__/useInspectionListPage.derivedCounts.test.tsx`
    bestPracticeCount: inspection.bestPracticeCount,
    // ⚠️ **이 한 줄이 행 메뉴 「보고서 생성」 게이트의 유일한 공급원이다 — 지우면 손계산으로 돌아간다.**
    // 위 `bestPracticeCount`와 같은 함정이다(옵셔널 prop이라 안 실으면 `undefined`로 조용히 떨어지고
    // tsc·spec 어느 쪽도 못 본다). 다만 결과가 반대다: 배지는 **숨지만** 이 값이 빠지면 소비처가
    // 폴백(`COMPLETED` + 권한)으로 떨어져 **문이 열린다** — 2026-10-08까지 그 상태였고, 완료·서명까지
    // 끝난 뒤 발견사항을 지워 선택이 0이 된 점검이 상세에서는 막히고 목록에서는 열렸다.
    // 출처는 서버 `findAll`의 파생값(`resolveInspectionReportGate`)이고 칼럼이 아니다.
    //
    // 탐지기: `__tests__/useInspectionListPage.derivedCounts.test.tsx`의 `[되돌림] 서버 게이트를
    // 그대로 싣는다`(위 `bestPracticeCount`와 **같은 자**로 잰다 — 그 축이 이 전사이기 때문이다).
    // ⚠️ **옆의 두 파일은 이 한 줄을 재지 «않는다»** — 2026-10-09까지 여기 그 둘이 적혀 있었는데
    // 하나는 **없는 파일**이었고(`desktop/__tests__/InspectionTable.reportGate.test.tsx`) 다른 하나는
    // 이 훅을 분모에 넣지 않았다(`lib/__tests__/reportGate.test.ts`의 `GATE_CONSUMERS` 7개에
    // `hooks/useInspectionListPage.ts` **0건** / [대조군] `DropdownMenu` 4건). 그 둘이 소유하는 것은
    // **소비처가 이 값을 읽는가**(⑦~⑬ 배선 래칫)이고 **그 값이 거기까지 실리는가**는 아무도 재지
    // 않았다 — 「파생이 옳다」·「게이트가 옳다」를 각각 재도 그 사이 배선은 재지지 않는다.
    reportGate: inspection.reportGate,
    status: inspection.status as LayoutInspectionStatus,
    contributorCount: inspection._count.collaborators ?? inspection.collaborators?.length ?? 1,
    collaborators: inspection.collaborators?.map((c) => ({
      // 목록 API(`findAll`)의 collaborators select에는 row `id`가 없다(userId·role·서명 스냅샷만).
      // 소비처(InspectionTable 담당자 칸)는 `inspection.inspectorId`(user id)와 대조해 점검자 본인을
      // 제외하므로 축도 user id가 맞다 — `c.id`를 쓰면 undefined라 key 경고 + 점검자가 「+1」로 세어졌다.
      id: c.userId,
      // 참여자 각각의 signerName ?? joinSnapshotName 우선, 없으면 라이브 라벨
      name: getEvidenceDisplayName((c as any).signerName ?? (c as any).joinSnapshotName ?? null, { name: c.userName || c.user?.member?.name }),
      role: c.role,
    })),
    thumbnailUrl: undefined, // TODO: 썸네일 URL 추가
    highestSeverity: getHighestSeverity(inspection.severityCounts),
    deletedAt: inspection.deletedAt,
    sequenceNumber: inspection.sequenceNumber,
    isLatestInspection: inspection.isLatestInspection,
    scheduledAt: inspection.scheduledAt,
  }));

  // inProgressInspections를 레이아웃 호환 타입으로 변환
  const layoutInProgressInspections = (inProgressInspections as ApiInProgressInspection[]).map((i) => ({
    id: i.id,
    siteId: i.siteId,
    siteName: i.siteName,
    inspectorId: i.inspectorId,
    inspectorName: i.inspectorName,
    status: i.status as LayoutInspectionStatus,
    photoCount: i.photoCount,
    findingCount: i.findingCount,
    contributorCount: i.collaboratorCount,
    startedAt: i.startedAt,
  }));

  return {
    // 데이터
    inspections: inspectionListItems,
    sites: sites.map(s => ({ id: s.id, name: s.name })),
    locations,
    equipment,
    inProgressInspections: layoutInProgressInspections,
    pagination,
    filters,
    currentUserId,
    isOwner,
    selectedSiteId,
    isLoading,
    error,
    sortField: apiFilters.sortField,
    sortDirection: apiFilters.sortDirection ?? 'desc',

    // 핸들러
    onFilterChange: handleFilterChange,
    onLoadMore: handleLoadMore,
    onPageChange: handlePageChange,
    onSort: handleSort,
    onRefresh: handleRefresh,
    onViewInspection: handleViewInspection,
    onDeleteInspection: handleDeleteInspection,
    onRestoreInspection: handleRestoreInspection,
    onRequestReview: handleRequestReview,
    onStartScheduled: handleStartScheduled,
    startScheduledConfirmOpen,
    onConfirmStartScheduled: handleConfirmStartScheduled,
    onCancelStartScheduled: () => setStartScheduledConfirmOpen(false),
    onStartNewClick: handleStartNewClick,
    onStartNew: handleStartNew,
    onContinueInspection: handleContinueInspection,
    onToggleIncludeDeleted: handleToggleIncludeDeleted,
  };
}

/**
 * 심각도 개수에서 가장 높은 심각도 반환
 */
function getHighestSeverity(
  counts?: { LOW: number; MEDIUM: number; HIGH: number; CRITICAL: number }
): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | undefined {
  if (!counts) return undefined;

  if (counts.CRITICAL > 0) return 'CRITICAL';
  if (counts.HIGH > 0) return 'HIGH';
  if (counts.MEDIUM > 0) return 'MEDIUM';
  if (counts.LOW > 0) return 'LOW';

  return undefined;
}
